import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BalancePoint } from '@/api/envelopes';
import { formatCivilMonthShort, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

const VIEW_W = 200;
const VIEW_H = 40;

interface BalanceTrendProps {
  /** Month-end balances, oldest first. An empty series draws nothing. */
  series: BalancePoint[];
  /** The virtual account owner's currency. */
  currency: string | null;
  /** Current balance. Below zero selects the negative stroke and wash. */
  balanceMinor: number | null;
}

/**
 * Prototype P:3419-3427. A series below zero closes the area to the zero line; otherwise it
 * closes to the first point. The solid wash replaces the prototype's opacity fade (TMM-T4),
 * so the zero line is painted above the wash.
 */
function chartGeometry(series: BalancePoint[]): {
  line: string;
  area: string;
  zeroY: string;
  mark: { cx: string; cy: string } | null;
} {
  const values = series.map((point) => point.balanceMinor);
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const span = Math.max(1, hi - lo);
  // One point has no span to divide. Place it at mid-width.
  const xAt = (index: number) =>
    series.length === 1 ? VIEW_W / 2 : (index * VIEW_W) / (series.length - 1);
  const yAt = (value: number) => VIEW_H - 2 - ((value - lo) / span) * (VIEW_H - 4);
  const line = series
    .map((point, index) => `${xAt(index).toFixed(1)},${yAt(point.balanceMinor).toFixed(1)}`)
    .join(' ');
  const closeY = yAt(lo < 0 ? 0 : (values[0] ?? 0)).toFixed(1);
  const lastX = xAt(series.length - 1).toFixed(1);
  const only = series.length === 1 ? series[0] : undefined;
  const mark =
    only === undefined ? null : { cx: xAt(0).toFixed(1), cy: yAt(only.balanceMinor).toFixed(1) };
  return {
    line,
    area: `0,${closeY} ${line} ${lastX},${closeY}`,
    zeroY: yAt(0).toFixed(1),
    mark,
  };
}

/**
 * Envelope balance area (prototype P:260-268): inline SVG, no chart library. 200×40, stretched to the
 * card width, 40px high. The card omits this when the series is null. An empty series draws nothing.
 * One point is a circle: a one-point polyline does not paint.
 */
export function BalanceTrend({ series, currency, balanceMinor }: BalanceTrendProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const first = series[0];
  const last = series[series.length - 1];
  if (series.length === 0 || first === undefined || last === undefined) return null;

  const { line, area, zeroY, mark } = chartGeometry(series);
  const negative = balanceMinor !== null && balanceMinor < 0;
  const stroke = negative ? tokens.moneyNegative : tokens.brandBoundary;
  const wash = negative ? tokens.moneyNegativeWash : tokens.brandSoft;

  return (
    <Box>
      <svg
        role="img"
        aria-label={t('envelope.balanceTrendLabel', {
          first: formatMoneyMinor(first.balanceMinor, currency, locale),
          last: formatMoneyMinor(last.balanceMinor, currency, locale),
        })}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        width="100%"
        height={VIEW_H}
        style={{ display: 'block' }}
      >
        <polygon points={area} fill={wash} />
        <line
          x1={0}
          y1={zeroY}
          x2={VIEW_W}
          y2={zeroY}
          stroke={tokens.line}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        <polyline
          points={line}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {mark !== null && <circle cx={mark.cx} cy={mark.cy} r={2} fill={stroke} />}
      </svg>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.375 }}>
        {/* v2 L274: 10px axis labels, "Oct '25" (the 0108 review); `text2` keeps them AA. */}
        <Typography component="span" sx={{ fontSize: 10, color: tokens.text2 }}>
          {formatCivilMonthShort(first.month, locale)}
        </Typography>
        <Typography component="span" sx={{ fontSize: 10, color: tokens.text2 }}>
          {formatCivilMonthShort(last.month, locale)}
        </Typography>
      </Box>
    </Box>
  );
}
